import { useState } from 'react';
import { trackError } from '#api/errors.ts';
import { useDeleteGoogleIdP, useGoogleIdPConnectionQuery, useStartGoogleIdP } from '#api/google-idp.queries.ts';
import { describeGoogleIdPError } from '#api/google-idp.ts';
import { useSuspenseFeatureFlagsQuery } from '#api/org.queries.ts';
import { AlertIcon, CheckCircle, InfoCircle } from '#assets/ReactSvg.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { DetailCard } from '#components/atoms/cards/detail-card/DetailCard.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { Pill } from '#components/atoms/pill/Pill.tsx';
import { Timestamp } from '#components/atoms/timestamp/Timestamp.tsx';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import { DetailList } from '#components/patterns/detail-list/DetailList.tsx';
import { DetailListRow } from '#components/patterns/detail-list/DetailListRow.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { EndpointOIDC } from '#dn-types/endpoint-oidc.ts';
import type { GoogleIdPConnection } from '#dn-types/google-idp.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { GoogleDirectorySyncButton } from './GoogleDirectorySyncButton.tsx';
import { GoogleDirectoryTestButton } from './GoogleDirectoryTestButton.tsx';
import { savePendingGoogleIdPFlow } from './pending-flow.ts';
import styles from './GoogleDirectorySyncCard.module.css';

type Props = {
  /** The Google provider this card is shown on. The connection itself belongs to the organization. */
  endpointOIDC: EndpointOIDC;
};

/**
 * {@link GoogleDirectorySyncCard} lets an admin connect the organization's Google Workspace directory, from
 * the page of a Google endpoint SSO provider, and shows the connection's health once it exists.
 */
export function GoogleDirectorySyncCard({ endpointOIDC }: Props) {
  const { data: { GOOGLE_IDP_DELETE_BLOCKED_DAYS: deleteAfterDays = 0 } = {} } = useSuspenseFeatureFlagsQuery();
  const justConnected = useAppLocation().state?.googleIdPConnected === true;
  const { data, isPending, isError, refetch } = useGoogleIdPConnectionQuery({ enabled: true });

  return (
    <DetailCard>
      <div className={styles.GoogleDirectorySyncCard_top}>
        <div>
          <h2 className="h4 mt-0 mb-8">Google Workspace directory sync</h2>
          <p className="p-md m-0">
            Keep access in step with your Google Workspace. When someone who signs in with Google is suspended, archived
            or deleted there, their hosts are blocked, and unblocked again if they are restored.
            {deleteAfterDays > 0 ?
              ` Hosts that stay blocked for ${deleteAfterDays} ${deleteAfterDays === 1 ? 'day' : 'days'} are deleted.`
            : ''}
          </p>
        </div>
        {data?.data ?
          <ConnectionStatusPill connection={data.data} />
        : null}
      </div>

      {isPending ?
        <p className="p-md">Loading…</p>
      : isError ?
        <Spacer direction="vertical">
          <ErrorMessage>We couldn’t load the directory connection.</ErrorMessage>
          <div>
            <Button variant="secondary" size="small" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        </Spacer>
      : data.data ?
        <ConnectedView connection={data.data} justConnected={justConnected} />
      : <NotConnectedView providerId={endpointOIDC.id} />}
    </DetailCard>
  );
}

/** Kept at full width: the description beside it would otherwise squeeze it down to an ellipsis. */
function ConnectionStatusPill({ connection }: { connection: GoogleIdPConnection }) {
  if (connection.status === 'error') {
    return (
      <Pill variant="alert" className="shrink-0">
        Connection error
      </Pill>
    );
  }
  if (connection.paused) {
    return <Pill className="shrink-0">Paused</Pill>;
  }
  return (
    <Pill variant="success" className="shrink-0">
      Syncing
    </Pill>
  );
}

function NotConnectedView({ providerId }: { providerId: string }) {
  const start = useStartGoogleIdP();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function connect() {
    setErrorMsg(null);
    try {
      const { redirectURL, requestToken } = await start.mutateAsync({ endpointOIDCProviderID: providerId });
      savePendingGoogleIdPFlow({ requestToken, providerId });
      globalThis.location.assign(redirectURL);
    } catch (error) {
      trackError(error);
      setErrorMsg(describeGoogleIdPError(error));
    }
  }

  return (
    <Spacer direction="vertical">
      <p className="p-md m-0">
        A Google Workspace super admin signs in to Google and grants read-only access to users and groups. Nothing in
        your Workspace is changed.
      </p>
      <div>
        <Button onClick={() => void connect()} disabled={start.isPending}>
          Connect Google Workspace
        </Button>
      </div>
      {errorMsg ?
        <ErrorMessage>{errorMsg}</ErrorMessage>
      : null}
    </Spacer>
  );
}

function ConnectedView({ connection, justConnected }: { connection: GoogleIdPConnection; justConnected: boolean }) {
  const [showDisconnect, setShowDisconnect] = useState(false);

  return (
    <Spacer direction="vertical">
      {justConnected ?
        <Banner variant="success" Icon={CheckCircle} iconClassName="color-success">
          Google Workspace connected.
        </Banner>
      : null}

      {connection.status === 'error' ?
        <Banner variant="alert" Icon={AlertIcon}>
          Google stopped accepting this connection. Disconnect it and connect again with a Google Workspace super admin
          account.
        </Banner>
      : null}

      {connection.paused ?
        <Banner variant="info" Icon={InfoCircle}>
          Sync is paused because none of your Google endpoint SSO providers are enabled. Enabling one resumes sync
          without reconnecting.
        </Banner>
      : null}

      {connection.domainMismatch ?
        <Banner variant="alert" Icon={AlertIcon}>
          The connected Workspace{connection.domain ? ` (${connection.domain})` : ''} does not match the verified domain
          of any Google endpoint SSO provider. Check that the right Workspace is connected; this is expected if your SSO
          domain is a secondary domain of that Workspace.
        </Banner>
      : null}

      <DetailList>
        <DetailListRow label="Connected by" value={connection.adminEmail} />
        <DetailListRow label="Workspace domain" value={connection.domain ?? '—'} />
        <DetailListRow
          label="Last successful read"
          value={connection.lastSyncAt ? <Timestamp datetime={connection.lastSyncAt} /> : 'Never'}
        />
        <DetailListRow label="Connected" value={<Timestamp datetime={connection.createdAt} />} />
      </DetailList>

      <p className="p-sm m-0">This connection is shared by all Google endpoint SSO providers in your organization.</p>

      <Spacer className={styles.GoogleDirectorySyncCard_actions}>
        {/* A paused connection would only report that it is paused, so don't offer it. */}
        <GoogleDirectorySyncButton variant="primary" disabled={connection.paused} />
        <GoogleDirectoryTestButton />
        <Button variant="danger" onClick={() => setShowDisconnect(true)}>
          Disconnect
        </Button>
      </Spacer>

      {showDisconnect ?
        <DisconnectDialog
          onClose={() => {
            setShowDisconnect(false);
          }}
        />
      : null}
    </Spacer>
  );
}

function DisconnectDialog({ onClose }: { onClose: () => void }) {
  const disconnect = useDeleteGoogleIdP();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  return (
    <ConfirmDialog
      title="Disconnect Google Workspace?"
      acceptButtonText="Disconnect"
      disableAccept={disconnect.isPending}
      onCancel={onClose}
      onAccept={async () => {
        setErrorMsg(null);
        try {
          await disconnect.mutateAsync();
          onClose();
        } catch (error) {
          trackError(error);
          setErrorMsg('We couldn’t disconnect the directory, please try again.');
        }
      }}
    >
      <p className="p mt-0">
        We will stop reading your Google Workspace directory and revoke our access at Google. Sign-ins are not affected.
        Hosts the sync already blocked stay blocked, and are no longer deleted automatically.
      </p>
      <p className="p">You can connect it again at any time.</p>
      {errorMsg ?
        <ErrorMessage className="mt-32">{errorMsg}</ErrorMessage>
      : null}
    </ConfirmDialog>
  );
}
